(() => {
  const root = document.documentElement;
  const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const dayLabel = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Europe/London'
  });
  const ukClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  const iso = date => date.toISOString().slice(0, 10);
  const dateUTC = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12));
  const plusDay = date => new Date(date.getTime() + 86400000);
  const plusDays = (date, days) => new Date(date.getTime() + days * 86400000);
  // Add one-off bank holidays or warehouse closure dates as YYYY-MM-DD values.
  const additionalBankHolidays = new Set([]);
  const bankHolidayCache = new Map();

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return dateUTC(year, month, day);
  }

  function weekdayInMonth(year, month, weekday, fromEnd = false) {
    let date = fromEnd ? dateUTC(year, month + 1, 0) : dateUTC(year, month, 1);
    const direction = fromEnd ? -1 : 1;
    while (date.getUTCDay() !== weekday) date = plusDays(date, direction);
    return date;
  }

  function englandWalesBankHolidays(year) {
    if (bankHolidayCache.has(year)) return bankHolidayCache.get(year);
    const days = new Set();
    const addObserved = date => {
      let observed = date;
      while (observed.getUTCDay() === 0 || observed.getUTCDay() === 6 || days.has(iso(observed))) observed = plusDay(observed);
      days.add(iso(observed));
    };

    addObserved(dateUTC(year, 1, 1));
    const easter = easterSunday(year);
    days.add(iso(plusDays(easter, -2)));
    days.add(iso(plusDays(easter, 1)));
    days.add(iso(weekdayInMonth(year, 5, 1)));
    days.add(iso(weekdayInMonth(year, 5, 1, true)));
    days.add(iso(weekdayInMonth(year, 8, 1, true)));
    addObserved(dateUTC(year, 12, 25));
    addObserved(dateUTC(year, 12, 26));
    bankHolidayCache.set(year, days);
    return days;
  }

  const isWorkingDay = date => date.getUTCDay() !== 0
    && date.getUTCDay() !== 6
    && !englandWalesBankHolidays(date.getUTCFullYear()).has(iso(date))
    && !additionalBankHolidays.has(iso(date));

  const nextWorkingDay = date => {
    let next = plusDay(date);
    while (!isWorkingDay(next)) next = plusDay(next);
    return next;
  };

  function moneyFromCents(cents, divisor = 1) {
    return gbp.format(cents / 100 / divisor);
  }

  function priceScope(display) {
    return display.closest('.product-form__info-content, .product-item__info-inner, .ic-accessory-card') || display.parentElement;
  }

  function renderPrice(display, mode) {
    const cents = Number(display.dataset.incCents);
    const max = Number(display.dataset.maxCents || cents);
    const compare = Number(display.dataset.compareCents || 0);
    const rate = Number(display.dataset.vatRate || 20);
    const taxable = display.dataset.taxable === 'true';
    const divisor = taxable ? 1 + rate / 100 : 1;
    if (!Number.isFinite(cents)) return;

    display.replaceChildren();
    display.append((max > cents ? 'From ' : '') + moneyFromCents(cents, divisor));
    if (max > cents) display.append(' – ' + moneyFromCents(max, divisor));
    if (compare > cents && max === cents) {
      const crossedOut = document.createElement('s');
      crossedOut.textContent = moneyFromCents(compare, divisor);
      display.append(' ', crossedOut);
    }
    const caption = document.createElement('small');
    caption.textContent = taxable ? `ex VAT (${rate}%)` : 'VAT-free';
    display.append(caption);
    display.hidden = mode !== 'ex';

    const scope = priceScope(display);
    const inclusive = scope?.querySelector('.price-list');
    const inclusiveLabel = scope?.querySelector('.ic-inc-vat-label');
    if (inclusive) inclusive.hidden = mode === 'ex';
    if (inclusiveLabel) inclusiveLabel.hidden = mode === 'ex';
  }

  function setPriceMode(mode, persist = true) {
    const exVat = mode === 'ex';
    root.dataset.icPriceMode = exVat ? 'ex' : 'inc';

    document.querySelectorAll('[data-ic-vat-toggle]').forEach(button => {
      button.setAttribute('aria-checked', String(exVat));
      button.setAttribute('aria-pressed', String(exVat));
      button.setAttribute('aria-label', exVat ? 'Show prices including VAT' : 'Show prices excluding VAT');
      const status = button.querySelector('[data-ic-vat-status]');
      if (status) status.textContent = exVat ? 'Prices currently exclude VAT' : 'Prices currently include VAT';
    });

    document.querySelectorAll('[data-ic-price-display]').forEach(display => renderPrice(display, exVat ? 'ex' : 'inc'));
    document.querySelectorAll('[data-price-ex-vat]').forEach(node => { node.hidden = !exVat; });
    document.querySelectorAll('[data-price-inc-vat]').forEach(node => { node.hidden = exVat; });

    if (persist) {
      try { localStorage.setItem('industracare-price-mode', exVat ? 'ex' : 'inc'); } catch (_) { /* Storage can be unavailable. */ }
    }
  }

  function initialiseVatControls(scope = document) {
    scope.querySelectorAll('[data-ic-vat-toggle]:not([data-ic-vat-bound])').forEach(button => {
      button.dataset.icVatBound = 'true';
      button.addEventListener('click', () => setPriceMode(root.dataset.icPriceMode === 'ex' ? 'inc' : 'ex'));
    });
    setPriceMode(root.dataset.icPriceMode === 'ex' ? 'ex' : 'inc', false);
  }

  let stored = 'inc';
  try { stored = localStorage.getItem('industracare-price-mode') === 'ex' ? 'ex' : 'inc'; } catch (_) { /* Default to VAT inclusive. */ }
  root.dataset.icPriceMode = stored;
  initialiseVatControls();

  function pad(value) { return String(value).padStart(2, '0'); }

  function deliveryState() {
    const parts = Object.fromEntries(ukClock.formatToParts(new Date()).filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
    const today = dateUTC(Number(parts.year), Number(parts.month), Number(parts.day));
    const secondsNow = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const beforeCutoff = isWorkingDay(today) && secondsNow < 15 * 3600;
    let dispatch;
    if (beforeCutoff) {
      dispatch = today;
    } else if (isWorkingDay(today)) {
      dispatch = nextWorkingDay(today);
    } else {
      dispatch = today;
      while (!isWorkingDay(dispatch)) dispatch = plusDay(dispatch);
    }
    const delivery = nextWorkingDay(dispatch);
    const remainingSeconds = beforeCutoff ? Math.max(0, 15 * 3600 - secondsNow) : 0;
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    return { beforeCutoff, dispatch, delivery, countdown: `${hours}h ${pad(minutes)}m ${pad(seconds)}s` };
  }

  function updateDelivery() {
    const state = deliveryState();
    document.querySelectorAll('[data-ic-delivery]:not([hidden])').forEach(node => {
      const title = node.querySelector('[data-ic-delivery-title]');
      const copy = node.querySelector('[data-ic-delivery-copy]');
      if (title) title.textContent = state.beforeCutoff
        ? `Order within ${state.countdown}`
        : `Dispatches ${dayLabel.format(state.dispatch)}`;
      if (copy) copy.textContent = state.beforeCutoff
        ? `For estimated delivery ${dayLabel.format(state.delivery)} on eligible stocked items.`
        : `Estimated delivery ${dayLabel.format(state.delivery)}. No dispatch or delivery on bank holidays.`;
    });

    document.querySelectorAll('[data-ic-deadline-countdown]').forEach(node => {
      node.textContent = state.beforeCutoff
        ? `${state.countdown} left to order for next-working-day delivery`
        : `Order now for dispatch ${dayLabel.format(state.dispatch)}`;
    });
  }

  updateDelivery();
  window.setInterval(updateDelivery, 1000);

  document.addEventListener('variant:changed', event => {
    const variant = event.detail?.variant;
    if (!variant) return;
    const region = event.target;
    const display = region.querySelector('[data-ic-price-display]');
    if (display) {
      display.dataset.incCents = variant.price;
      display.dataset.maxCents = variant.price;
      display.dataset.compareCents = variant.compare_at_price || 0;
      display.dataset.taxable = String(variant.taxable);
      try {
        display.dataset.vatRate = JSON.parse(region.querySelector('[data-ic-vat-rates]').textContent)[variant.id] ?? 20;
      } catch (_) {
        display.dataset.vatRate = 20;
      }
      setPriceMode(root.dataset.icPriceMode, false);
    }
    const promise = region.querySelector('[data-ic-delivery]');
    if (promise) {
      promise.hidden = !(variant.available && variant.inventory_management && Number(variant.inventory_quantity) > 0);
      updateDelivery();
    }
  });

  document.addEventListener('shopify:section:load', event => initialiseVatControls(event.target));

  const observer = new MutationObserver(mutations => {
    const selector = '[data-ic-vat-toggle], [data-ic-price-display], [data-price-ex-vat], [data-price-inc-vat]';
    const needsRefresh = mutations.some(mutation => [...mutation.addedNodes].some(node =>
      node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
    ));
    if (needsRefresh) initialiseVatControls();
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
